import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Challenge } from '../services/adminService'
import { BulkTranslationLanguageSelector } from './BulkTranslationLanguageSelector'

interface ChallengeBulkTranslationModalProps {
  challenges: Challenge[]
  onTranslate: (sourceLanguage: string, targetLanguages: string[], selectedChallenges: string[], maxConcurrency?: number) => void
  onClose: () => void
}

export function ChallengeBulkTranslationModal({ challenges, onTranslate, onClose }: ChallengeBulkTranslationModalProps) {
  const { t } = useTranslation()
  const [sourceLanguage, setSourceLanguage] = useState('en')
  const [targetLanguages, setTargetLanguages] = useState<string[]>([])
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>(challenges.map(t => t.id))
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  const availableTargetLanguages = SUPPORTED_LANGUAGES.filter(lang => lang.code !== sourceLanguage)

  const handleTargetLanguageToggle = (languageCode: string) => {
    setTargetLanguages(prev => 
      prev.includes(languageCode)
        ? prev.filter(code => code !== languageCode)
        : [...prev, languageCode]
    )
  }

  const handleChallengeToggle = (challengeId: string) => {
    setSelectedChallenges(prev => 
      prev.includes(challengeId)
        ? prev.filter(id => id !== challengeId)
        : [...prev, challengeId]
    )
  }

  const handleSelectAllChallenges = () => {
    if (selectedChallenges.length === challenges.length) {
      setSelectedChallenges([])
    } else {
      setSelectedChallenges(challenges.map(t => t.id))
    }
  }

  const selectAllTargets = () => {
    setTargetLanguages(availableTargetLanguages.map(l => l.code))
  }

  const clearAllTargets = () => {
    setTargetLanguages([])
  }

  const handleTranslate = () => {
    if (targetLanguages.length === 0) {
      toast.error(t('translations.selectTargetLanguage'))
      return
    }

    if (selectedChallenges.length === 0) {
      toast.error(t('challenges.selectChallenge'))
      return
    }

    const totalTranslations = selectedChallenges.length * targetLanguages.length
    const estimatedTime = Math.ceil(totalTranslations * 3) // 3 seconds per translation

    toast.success(
      t('translations.startingBulkTranslation', { 
        categories: selectedChallenges.length, 
        languages: targetLanguages.length, 
        time: estimatedTime 
      })
    )

    onTranslate(sourceLanguage, targetLanguages, selectedChallenges, 3) // Fixed max concurrency
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🚀</span>
              {t('challenges.bulkTranslation')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Language Selection */}
          <BulkTranslationLanguageSelector
            sourceLanguage={sourceLanguage}
            targetLanguages={targetLanguages}
            languages={SUPPORTED_LANGUAGES}
            onSourceLanguageChange={setSourceLanguage}
            onTargetLanguageToggle={handleTargetLanguageToggle}
            onSelectAllTargets={selectAllTargets}
            onClearAllTargets={clearAllTargets}
          />

          {/* Challenge Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('challenges.toTranslate')} ({selectedChallenges.length} {t('translations.selected')})
              </label>
              <button
                onClick={handleSelectAllChallenges}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {selectedChallenges.length === challenges.length ? t('actions.clearAll') : t('actions.selectAll')}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {challenges.map((challenge) => (
                <label key={challenge.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={selectedChallenges.includes(challenge.id)}
                    onChange={() => handleChallengeToggle(challenge.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-2xl">{challenge.icon}</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{challenge.name || challenge.id}</div>
                    {challenge.description && (
                      <div className="text-sm text-gray-500">{challenge.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleTranslate}
            disabled={selectedChallenges.length === 0 || targetLanguages.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center"
          >
            <span className="mr-2">🚀</span>
            {t('translations.startBulkTranslation')}
          </button>
          </div>
        </div>
      </div>
    </div>
  )
} 